import { Editor } from "@tinymce/tinymce-react";
import "tinymce/tinymce";
import "tinymce/models/dom/model";
import "tinymce/themes/silver";
import "tinymce/icons/default";
import "tinymce/skins/ui/oxide/skin";
import "tinymce/skins/content/default/content";
import "tinymce/skins/ui/oxide/content";
import "tinymce/plugins/advlist";
import "tinymce/plugins/autolink";
import "tinymce/plugins/charmap";
import "tinymce/plugins/image";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/table";
import "tinymce/plugins/wordcount";

const danishEditorTranslations = {
  "Undo": "Fortryd",
  "Redo": "Gentag",
  "Blocks": "Afsnit",
  "Paragraph": "Brødtekst",
  "Heading 1": "Overskrift 1",
  "Heading 2": "Overskrift 2",
  "Heading 3": "Overskrift 3",
  "Heading 4": "Overskrift 4",
  "Heading 5": "Overskrift 5",
  "Heading 6": "Overskrift 6",
  "Bold": "Fed",
  "Italic": "Kursiv",
  "Underline": "Understregning",
  "Strikethrough": "Gennemstregning",
  "Align left": "Venstrejuster",
  "Align center": "Centrer",
  "Align right": "Højrejuster",
  "Justify": "Lige margener",
  "Bullet list": "Punktopstilling",
  "Numbered list": "Nummereret liste",
  "Decrease indent": "Formindsk indrykning",
  "Increase indent": "Forøg indrykning",
  "Insert/edit link": "Indsæt/redigér link",
  "Insert/Edit Link": "Indsæt/redigér link",
  "Insert/edit image": "Indsæt/redigér billede",
  "Insert/Edit Image": "Indsæt/redigér billede",
  "Insert table": "Indsæt tabel",
  "Table": "Tabel",
  "Clear formatting": "Ryd formatering",
  "Remove link": "Fjern link",
  "Open link": "Åbn link",
  "Link": "Link",
  "Image": "Billede",
  "Browse files": "Gennemse filer",
  "Alternative description": "Alternativ beskrivelse",
  "Source": "Kilde",
  "Dimensions": "Dimensioner",
  "Constrain proportions": "Bevar proportioner",
  "Save": "Gem",
  "Cancel": "Annuller",
  "Close": "Luk",
  "Insert": "Indsæt",
  "Edit": "Redigér",
  "Delete": "Slet",
  "Remove": "Fjern",
  "Find and replace": "Søg og erstat",
  "Find": "Søg",
  "Replace": "Erstat",
  "Replace all": "Erstat alle",
  "Match case": "Forskel på store og små bogstaver",
  "Find whole words only": "Søg kun efter hele ord",
  "Previous": "Forrige",
  "Next": "Næste",
  "Special character": "Specialtegn",
  "Word Count": "Ordoptælling",
  "Words: {0}": "Ord: {0}",
  "Characters: {0}": "Tegn: {0}",
  "Characters (no spaces): {0}": "Tegn (uden mellemrum): {0}",
  "OK": "OK",
  "Yes": "Ja",
  "No": "Nej"
};

if (typeof window !== "undefined" && window.tinymce) {
  window.tinymce.addI18n("da", danishEditorTranslations);
}

function escapeHtml(value = "") {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function inlineMarkdown(text = "") {
  return text
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/__([^_]+)__/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/_([^_]+)_/g, "<em>$1</em>");
}

function renderLegacyMarkdown(markdown = "") {
  const escaped = escapeHtml(markdown).replace(/\r\n?/g, "\n");
  const lines = escaped.split("\n");
  const html = [];
  let listType = null;
  let paragraph = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    html.push(`<p>${inlineMarkdown(paragraph.join("<br />"))}</p>`);
    paragraph = [];
  };

  const closeList = () => {
    if (!listType) return;
    html.push(`</${listType}>`);
    listType = null;
  };

  lines.forEach((line) => {
    const trimmed = line.trim();

    if (trimmed === "") {
      flushParagraph();
      closeList();
      return;
    }

    if (/^---+$/.test(trimmed)) {
      flushParagraph();
      closeList();
      html.push("<hr />");
      return;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      flushParagraph();
      closeList();
      const level = headingMatch[1].length;
      html.push(`<h${level}>${inlineMarkdown(headingMatch[2])}</h${level}>`);
      return;
    }

    const unorderedMatch = trimmed.match(/^[-*]\s+(.+)$/);
    if (unorderedMatch) {
      flushParagraph();
      if (listType !== "ul") {
        closeList();
        listType = "ul";
        html.push("<ul>");
      }
      html.push(`<li>${inlineMarkdown(unorderedMatch[1])}</li>`);
      return;
    }

    const orderedMatch = trimmed.match(/^\d+\.\s+(.+)$/);
    if (orderedMatch) {
      flushParagraph();
      if (listType !== "ol") {
        closeList();
        listType = "ol";
        html.push("<ol>");
      }
      html.push(`<li>${inlineMarkdown(orderedMatch[1])}</li>`);
      return;
    }

    closeList();
    paragraph.push(trimmed);
  });

  flushParagraph();
  closeList();

  return html.join("");
}

export function normalizeRichText(value = "") {
  if (!value.trim()) return "";
  if (/<[a-z][\s\S]*>/i.test(value)) return value;

  return renderLegacyMarkdown(value);
}

export function renderRichText(value = "") {
  return normalizeRichText(value);
}

const editorContentStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;500;600;700&family=Montserrat:wght@400;500;600;700&display=swap');

  html {
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: rgba(100, 116, 139, 0.46) transparent;
  }
  body {
    margin: 0;
    width: 100%;
    padding: 2cm 18mm 20mm;
    box-sizing: border-box;
    overflow-x: hidden;
    color: #111827;
    font-family: "Lato", Arial, Helvetica, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.6;
  }
  ::-webkit-scrollbar { width: 0.7rem; height: 0.7rem; }
  ::-webkit-scrollbar-track, ::-webkit-scrollbar-corner { background: transparent; }
  ::-webkit-scrollbar-thumb { min-height: 2rem; border: 0.2rem solid transparent; border-radius: 999px; background: rgba(100, 116, 139, 0.46); background-clip: padding-box; }
  ::-webkit-scrollbar-thumb:hover { background-color: rgba(71, 85, 105, 0.68); }
  ::-webkit-scrollbar-thumb:active { background-color: rgba(51, 65, 85, 0.78); }
  h1 { color: #0f172a; font-family: "Montserrat", Arial, Helvetica, sans-serif; font-size: 21px; font-weight: 600; margin: 12px 0 6px; line-height: 1.2; }
  h2, h3, h4, h5, h6 { color: #0f172a; font-family: "Montserrat", Arial, Helvetica, sans-serif; font-weight: 600; margin: 12px 0 6px; line-height: 1.25; }
  h2 { font-size: 18px; }
  h3 { font-size: 16px; }
  h1:first-child, h2:first-child, h3:first-child, h4:first-child, h5:first-child, h6:first-child { margin-top: 0; }
  p, ul, ol, pre, blockquote { margin: 0; }
  ul, ol { padding-left: 20px; }
  img { max-width: 100%; height: auto; }
  code, pre { font-family: Consolas, Monaco, monospace; }
  pre { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; white-space: pre-wrap; }
  table { width: 100%; border-collapse: collapse; margin: 0 0 12px; }
  th, td { border: 1px solid #cbd5e1; padding: 8px; text-align: left; vertical-align: top; }
  hr { border: 0; border-top: 1px solid #cbd5e1; margin: 20px 0; }
`;

const editorPlugins = "advlist autolink charmap image link lists searchreplace table wordcount";
const editorToolbar = "undo redo | blocks | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image table | removeformat";

export default function RichTextEditor({ value = "", onChange = () => {}, disabled = false, toolbarContainerId = null, showToolbar = true }) {
  const usesExternalToolbar = Boolean(toolbarContainerId);
  const toolbar = showToolbar ? editorToolbar : false;

  return (
    <div className={`workspace-doc-editor__panel content-library-rich-text-editor ${usesExternalToolbar ? "content-library-rich-text-editor--inline" : ""}`.trim()}>
      <Editor
        licenseKey="gpl"
        inline={usesExternalToolbar}
        tagName={usesExternalToolbar ? "div" : undefined}
        value={value}
        disabled={disabled}
        onEditorChange={onChange}
        rollback={false}
        plugins={editorPlugins}
        toolbar={toolbar}
        init={{
          ...(usesExternalToolbar ? {
            fixed_toolbar_container: `#${toolbarContainerId}`,
            toolbar_persist: true,
            toolbar_sticky: false
          } : { height: 864 }),
          menubar: false,
          branding: false,
          promotion: false,
          statusbar: false,
          resize: false,
          language: "da",
          language_load: false,
          toolbar_mode: usesExternalToolbar ? "wrap" : "sliding",
          toolbar,
          block_formats: "Brødtekst=p; Overskrift 1=h1; Overskrift 2=h2; Overskrift 3=h3",
          ...(usesExternalToolbar ? {} : { content_style: editorContentStyle }),
          automatic_uploads: false,
          paste_data_images: false,
          paste_webkit_styles: "none",
          invalid_styles: {
            "*": "font-family font-size color background-color"
          },
          image_advtab: false,
          image_description: false,
          object_resizing: true
        }}
      />
    </div>
  );
}
